import { RECORD_GRID_CLASS } from 'components/record-header/RecordHeader';
import { FC, useMemo } from 'react';

import GridItemList, { infoLabel, Row } from 'components/GridItemList';
import { FormattedDate } from 'components/misc/FormattedDate';
import Unmasked from 'components/Unmasked';
import upperFirst from 'lodash/upperFirst';
import { getCaseChips, getLinkChips, getLinks } from 'pages/matters/util';
import { ClassifierData } from 'types/classifiers';
import { CorrespondenceDetailsDto, CorrespondenceDirectionType } from 'types/correspondence';
import { FeatureFlag } from 'types/features.ts';
import { getClassifierText, t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { sortByField } from 'utils/sort';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import CorrespondenceAssignmentItem from '../CorrespondenceAssignmentItem';
import { CorrespondenceSectionId } from '../correspondenceFormMapping';
import CorrespondenceMatterItem from '../CorrespondenceMatterItem';
import { CorrespondenceSection } from '../form-sections/CorrespondenceSection';
import { useCorrespondenceParties } from '../useCorrespondence';
import { getParties } from '../util/parties';

interface Props {
    correspondence: CorrespondenceDetailsDto;
    classifiers: Record<string, ClassifierData>;
    /** The new design: one section per topic, each edited in place. */
    isNew?: boolean;
}

const GeneralView: FC<Props> = ({ correspondence, classifiers, isNew = false }) => {
    const isCaseAssignmentEnabled = useIsFeatureEnabled(FeatureFlag.CASE_ASSIGNMENT_ENABLED);
    const canReadAssignments = useHasPermissions(['READ.MATTER_ASSIGNMENTS']);

    const recipientOrSenderStringEmployee =
        correspondence.directionTypeCode === CorrespondenceDirectionType.INCOMING ? 'recipient' : 'sender';

    const recipientOrSenderStringContact =
        correspondence.directionTypeCode === CorrespondenceDirectionType.INCOMING ? 'sender' : 'recipient';

    const { clientParties, senderOrRecipientParties } = useCorrespondenceParties(correspondence.parties);

    const sortedMatters = useMemo(
        () =>
            Array.from(correspondence.matters || []).sort((a, b) =>
                a.fullName.toLocaleUpperCase() < b.fullName.toLocaleUpperCase() ? -1 : 0
            ),
        [correspondence.matters]
    );

    const sortedAssignments = useMemo(
        () =>
            Array.from(correspondence.assignments || []).sort((a, b) => {
                const aLabel = `${a.name} ${a.matter?.fullName || ''}`.toLocaleUpperCase();
                const bLabel = `${b.name} ${b.matter?.fullName || ''}`.toLocaleUpperCase();
                return aLabel.localeCompare(bLabel);
            }),
        [correspondence.assignments]
    );

    const rows = useMemo(
        () => ({
            number: {
                label: t('correspondence.correspondence-number'),
                content: <Unmasked>{correspondence.number || '-'}</Unmasked>,
            },
            name: { label: t('correspondence.correspondence-name'), content: correspondence.name || '-' },
            type: {
                label: t('correspondence.correspondence-type'),
                content: getClassifierText(classifiers['CORRESPONDENCE_TYPE'], correspondence.typeCode) || '-',
            },
            category: {
                label: t('correspondence.correspondence-category'),
                content:
                    getClassifierText(classifiers['CORRESPONDENCE_CATEGORY_TYPE'], correspondence.categoryTypeCode) ||
                    '-',
            },
            direction: {
                label: t('correspondence.direction'),
                content:
                    getClassifierText(classifiers['CORRESPONDENCE_DIRECTION'], correspondence.directionTypeCode) || '-',
            },
            date: {
                label: t(`correspondence.${(correspondence.directionTypeCode ?? '').toLocaleLowerCase()}-date`),
                content: correspondence.date || '-',
            },
            documentDate: { label: t('correspondence.document-date'), content: correspondence.documentDate || '-' },
            employees: {
                label: t('correspondence.sender-recipient-employees', {
                    direction: upperFirst(recipientOrSenderStringEmployee),
                }),
                content: getLinks('employee', correspondence.employees?.map(({ employee }) => employee) ?? []),
            },
            contacts: {
                label: t(`correspondence.${recipientOrSenderStringContact}`),
                content: getLinks('contact', getParties(senderOrRecipientParties)),
            },
            official: { label: t('correspondence.official'), content: correspondence.isOfficial ? t('yes') : t('no') },
            officialReference: {
                label: t('correspondence.official-reference-number'),
                content: correspondence.officialReference || '-',
            },
            note: {
                label: t('correspondence.correspondence-notes'),
                content: correspondence.note || '-',
                multiline: true,
            },
            handled: {
                label: t('correspondence.handled'),
                content: correspondence.isHandled ? t('yes') : t('no'),
            },
            status: {
                label: t('correspondence.status'),
                content:
                    getClassifierText(classifiers['CORRESPONDENCE_STATUS_TYPE'], correspondence.statusTypeCode) || '-',
            },
        }),
        [
            correspondence,
            classifiers,
            recipientOrSenderStringEmployee,
            recipientOrSenderStringContact,
            senderOrRecipientParties,
        ]
    );

    const overviewData: Row[] = [
        rows.number,
        rows.name,
        rows.type,
        rows.category,
        rows.direction,
        rows.date,
        rows.documentDate,
        rows.employees,
        rows.contacts,
        rows.official,
        rows.officialReference,
        rows.note,
        rows.handled,
        rows.status,
    ];

    const connectedClientCaseData = useMemo<Row[]>(
        () => [
            {
                label: t('correspondence.connected-cases'),
                content:
                    sortedMatters.length > 0 ? (
                        <div className="flex flex-col">
                            {sortedMatters.map((matter) => (
                                <CorrespondenceMatterItem key={matter.id} matter={matter} />
                            ))}
                        </div>
                    ) : (
                        '-'
                    ),
            },
            {
                label: t('correspondence.connected-assignments'),
                content:
                    sortedAssignments.length > 0 ? (
                        <div className="flex flex-col">
                            {sortedAssignments.map((assignment) => (
                                <CorrespondenceAssignmentItem key={assignment.id} assignment={assignment} />
                            ))}
                        </div>
                    ) : (
                        '-'
                    ),
                hidden: !isCaseAssignmentEnabled,
            },
        ],
        [sortedMatters, sortedAssignments, isCaseAssignmentEnabled]
    );

    const dmsData = useMemo<Row[]>(
        () => [
            {
                label: t('correspondence.dms-link'),
                content: correspondence.dmsLink ? (
                    <a
                        className="entity-link text-fg hover:text-link no-underline hover:underline"
                        href={correspondence.dmsLink}
                    >
                        {correspondence.dmsLink}
                    </a>
                ) : (
                    '-'
                ),
            },
        ],
        [correspondence]
    );

    // The new design lists each card's fields in its form's order, under the form's labels and where the form
    // shows them, with a multi-select's values as the chips the field draws, so a row and its field share a cell.
    if (isNew) {
        const clients = clientParties
            .flatMap(({ mainParty, extraParty }) => [mainParty, extraParty])
            .filter((party) => party !== null);
        const matters = correspondence.matters ?? [];
        // A row under its field's label and info icon, the icon at the end of the label's line as the field has it.
        const hinted = (row: Row, labelId: string, tooltipId: string): Row => ({
            ...row,
            label: infoLabel(labelId, tooltipId, 'justify-between'),
        });
        const section = (id: CorrespondenceSectionId, titleId: string, data: Row[]) => (
            <CorrespondenceSection id={id} titleId={titleId} correspondence={correspondence}>
                {(edit) => <GridItemList isNew titleId={titleId} data={data} button={edit} />}
            </CorrespondenceSection>
        );
        return (
            <div className={RECORD_GRID_CLASS}>
                <div className="flex min-w-0 flex-col gap-(--card-gap)">
                    {section('general', 'correspondence.general', [
                        rows.number,
                        rows.name,
                        rows.type,
                        // Its radio options take a row of their own, whatever is picked.
                        { ...rows.direction, wide: 'always' },
                        rows.category,
                        // In the format the date fields print, from the source they read it from.
                        { ...rows.date, content: <FormattedDate value={correspondence.date} /> },
                        { ...rows.documentDate, content: <FormattedDate value={correspondence.documentDate} /> },
                        hinted(
                            rows.handled,
                            'correspondence.handled',
                            'correspondence.tooltips.handled-ready-for-further-activities'
                        ),
                        rows.status,
                    ])}
                    {section('senders', 'correspondence.sender-recipient', [
                        {
                            ...rows.employees,
                            content: '-',
                            chips: getLinkChips(
                                'employee',
                                sortByField(
                                    (correspondence.employees ?? []).map(({ employee }) => employee),
                                    'name'
                                )
                            ),
                        },
                        rows.contacts,
                    ])}
                    {section('note', 'note', [rows.note])}
                </div>
                <div className="flex min-w-0 flex-col gap-(--card-gap)">
                    {section('official', 'correspondence.official', [
                        hinted(
                            rows.official,
                            'correspondence.official',
                            'correspondence.tooltips.official-heightened-attention'
                        ),
                        {
                            ...hinted(
                                rows.officialReference,
                                'correspondence.official-reference-number',
                                'correspondence.tooltips.official-reference-number'
                            ),
                            hidden: !correspondence.isOfficial,
                        },
                    ])}
                    {section('connected', 'correspondence.connected-client-case', [
                        {
                            label: t('correspondence.client-name'),
                            content: '-',
                            chips: getLinkChips('contact', sortByField(clients, 'name')),
                        },
                        {
                            label: t('correspondence.case'),
                            content: '-',
                            chips: getCaseChips(matters),
                        },
                        {
                            label: t('correspondence.form.assignments'),
                            content: '-',
                            chips: (correspondence.assignments ?? []).map(({ id, name }) => ({ key: id, label: name })),
                            // The form offers assignments once a case is chosen, to who may read them.
                            hidden: matters.length === 0 || !canReadAssignments || !isCaseAssignmentEnabled,
                        },
                    ])}
                    {section('dms', 'correspondence.dms', dmsData)}
                </div>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
                <GridItemList titleId="correspondence.general" size={12} data={overviewData} key="matter-overview" />
            </div>
            <div>
                <GridItemList
                    titleId="correspondence.connected-client-case"
                    size={12}
                    data={connectedClientCaseData}
                    key="matter-connected-client-case"
                />
                <GridItemList className="mt-6" titleId="correspondence.dms" size={12} data={dmsData} key="matter-dms" />
            </div>
        </div>
    );
};

export default GeneralView;
